/**
 * Brokerage-conditional field gating.
 *
 * Several household / member / account fields are only mandatory (and therefore
 * only shown) when the project matches a specific brokerage configuration. The
 * two gating flags mirror the backend definitions exactly:
 *
 *   is_fidelity_nfs = project.custodian === "Fidelity" && custodian_types.nfs === true
 *   is_new_edge     = project.destination_firm === "NewEdge"
 *
 * Both flags are independent — a project can satisfy neither, one, or both.
 *
 * `custodian`, `custodian_types` and `destination_firm` are project-level fields
 * that the backend now also includes on the household payload, so most callers
 * can derive the flags directly from the household / wizard-data object via
 * `getBrokerageFlags(household)`. Where a caller only has the pieces as separate
 * props (e.g. a `custodianTypes` prop), use `computeBrokerageFlags(...)`.
 */

export const FIDELITY_CUSTODIAN = "Fidelity";
export const GOLDMAN_CUSTODIAN = "Goldman Sachs";
export const NEW_EDGE_FIRM = "NewEdge";
export const WAVERLY_FIRM = "Waverly";

/** Qualifying-question toggles that only Goldman Sachs projects collect. */
export const GOLDMAN_QUALIFIER_KEYS = [
  "finra_affiliation",
  "marijuana_securities",
  "politically_exposed_person",
];

/** The four investor-experience fields that are only required for Fidelity NFS or NewEdge. */
export const CONDITIONAL_EXPERIENCE_KEYS = [
  "alternative_investments",
  "variable_annuities",
  "mutual_funds",
  "partnerships",
];

/** Investor-experience fields that remain always required regardless of brokerage. */
export const ALWAYS_EXPERIENCE_KEYS = ["stocks", "bonds", "options"];

export function computeBrokerageFlags({
  custodian,
  custodianTypes,
  destinationFirm,
} = {}) {
  const isFidelityNfs =
    custodian === FIDELITY_CUSTODIAN && custodianTypes?.nfs === true;
  const isFidelityIws =
    custodian === FIDELITY_CUSTODIAN && custodianTypes?.iws === true;
  const isGoldman = custodian === GOLDMAN_CUSTODIAN;
  const isNewEdge = destinationFirm === NEW_EDGE_FIRM;
  const isWaverly = destinationFirm === WAVERLY_FIRM;
  return { isFidelityNfs, isFidelityIws, isGoldman, isNewEdge, isWaverly };
}

/** Derive the flags from any household / project / wizard-data-like object. */
export function getBrokerageFlags(source) {
  return computeBrokerageFlags({
    custodian: source?.custodian,
    custodianTypes: source?.custodian_types,
    destinationFirm: source?.destination_firm,
  });
}

/* ─── Per-field predicates ──────────────────────────────────────────────────
 * Because unrequired fields are hidden entirely, "should show" === "required".
 * Each accepts the flags object returned by getBrokerageFlags / computeBrokerageFlags.
 */

// Group A — Fidelity NFS only
export const showIdInfo = (f) => !!f?.isFidelityNfs;
export const showAnnualExpenseRange = (f) => !!f?.isFidelityNfs;
export const showSpecialExpenseRange = (f) => !!f?.isFidelityNfs;
export const showMaritalStatus = (f) => !!f?.isFidelityNfs;

// Group B — Fidelity NFS OR NewEdge
export const showSourceOfFunds = (f) => !!f?.isFidelityNfs || !!f?.isNewEdge;
export const showConditionalExperience = (f) =>
  !!f?.isFidelityNfs || !!f?.isNewEdge;

// Group C — NewEdge only
export const showUseOfFunds = (f) => !!f?.isNewEdge;
export const showLiquidityRequirements = (f) => !!f?.isNewEdge;

// NFS field — hidden only for IWS-only projects (strict wins when both IWS and NFS are enabled)
export const showAccountDecisionMaking = (f) => !f?.isFidelityIws || !!f?.isFidelityNfs;

// Derived from tax form (1040) — hidden for IWS-only projects (strict wins when both IWS and NFS are enabled)
export const showTaxBracket = (f) => !f?.isFidelityIws || !!f?.isFidelityNfs;

// Fields that stay visible but are not mandatory for IWS-only projects (strict wins when NFS is also enabled)
export const isFieldOptionalForIws = (f) => !!f?.isFidelityIws && !f?.isFidelityNfs;

// Group E — Goldman Sachs only
export const showGoldmanQualifiers = (f) => !!f?.isGoldman;
export const showStatutoryTrust = (f) => !!f?.isGoldman;
export const showBankingEstimates = (f) => !!f?.isGoldman;
export const showHouseholdSourceOfWealth = (f) => !!f?.isGoldman;

// Group D — NewEdge, or Fidelity IWS when options trading is enabled
export const accountHasOptions = (account) => account?.options === true;
export const anyAccountHasOptions = (accounts) =>
  (accounts || []).some(accountHasOptions);
export const showIncomeDetails = (f, accounts) =>
  !!f?.isNewEdge || (!!f?.isFidelityIws && anyAccountHasOptions(accounts));
export const showCoreExperience = (f, accounts) =>
  showIncomeDetails(f, accounts);
export const showStrategyDefaults = (f, accounts) =>
  showIncomeDetails(f, accounts);
export const showAccountRiskDetails = (f, account) =>
  !!f?.isNewEdge || (!!f?.isFidelityIws && accountHasOptions(account));

/** Whether a single investor_experience field should be shown for the given flags. */
export const showExperienceField = (key, f, accounts) =>
  CONDITIONAL_EXPERIENCE_KEYS.includes(key)
    ? showConditionalExperience(f)
    : showCoreExperience(f, accounts);

/** Filter a list of investor_experience field defs down to the ones to render. */
export const filterExperienceFields = (fields, f, accounts, getKey = (x) => x?.key ?? x) =>
  (fields || []).filter((field) => showExperienceField(getKey(field), f, accounts));
